// src/components/Packaging/PackagingRules.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePackaging } from "../../contexts/PackagingContext";
import PackagingRuleModal from "./PackagingRuleModal";

export default function PackagingRules() {
  const navigate = useNavigate();
  const { items, rules, addRule, updateRule, deleteRule } = usePackaging();

  // page filters
  const [scope, setScope] = useState("global"); // 'global' | 'product' | 'batch'
  const [productId, setProductId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [search, setSearch] = useState("");

  // modal state
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // rule object or null

  const itemMap = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);

  const filteredRules = useMemo(() => {
    let list = rules;

    // scope filter
    if (scope === "global") list = list.filter(r => r.scope === "global");
    if (scope === "product") list = list.filter(r => r.scope === "product" && (!productId || r.productId === productId));
    if (scope === "batch") list = list.filter(r => r.scope === "batch" && (!batchId || r.batchId === batchId));

    // search by item name/category
    const q = search.trim().toLowerCase();
    if (!q) return list;

    return list.filter(r => {
      const it = itemMap.get(r.itemId);
      const hay = [(it?.name || ""), (it?.category || "")].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rules, scope, productId, batchId, search, itemMap]);

  // open add
  const onAdd = () => {
    setEditing(null);
    setOpen(true);
  };

  // open edit
  const onEdit = (rule) => {
    setEditing(rule);
    setOpen(true);
  };

  // save from modal
  const handleSave = (payload) => {
    if (editing) {
      // editing existing → update
      updateRule(editing.id, {
        ...payload,
        // preserve original scope targeting fields if modal didn't set them
        scope: payload.scope ?? editing.scope,
        productId: payload.scope === "product" ? payload.productId ?? editing.productId ?? null : null,
        batchId: payload.scope === "batch" ? payload.batchId ?? editing.batchId ?? null : null,
      });
    } else {
      // creating new → ensure scope linking based on current page controls
      const base = {
        scope,
        productId: scope === "product" ? (productId || null) : null,
        batchId: scope === "batch" ? (batchId || null) : null,
        active: true,
      };
      addRule({ ...base, ...payload });
    }
    setOpen(false);
    setEditing(null);
  };

  return (
    <div className="p-6 text-white">
      {/* header */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-semibold">Packaging Rules</h1>
        <button
          onClick={() => navigate("/dashboard")}
          className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600"
        >
          ← Back to Dashboard
        </button>
      </div>
      <div className="text-xs opacity-70 mb-4">
        Link items from your Packaging Library, then define how much is used per unit, batch, or thresholds.
      </div>

      {/* controls */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
        <div>
          <label className="block text-xs mb-1">Scope</label>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="w-full px-3 py-2 rounded bg-slate-800"
          >
            <option value="global">Global (defaults)</option>
            <option value="product">Product-specific</option>
            <option value="batch">Batch-specific</option>
          </select>
        </div>

        {scope === "product" && (
          <div className="md:col-span-2">
            <label className="block text-xs mb-1">Product ID</label>
            <input
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-800"
              placeholder="e.g. product_123"
            />
          </div>
        )}

        {scope === "batch" && (
          <div className="md:col-span-2">
            <label className="block text-xs mb-1">Batch ID</label>
            <input
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-800"
              placeholder="e.g. batch_2025_09_01"
            />
          </div>
        )}

        <div className={scope === "global" ? "md:col-span-3" : "md:col-span-1"}>
          <label className="block text-xs mb-1">Search Items</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 rounded bg-slate-800"
            placeholder="Filter by name/category…"
          />
        </div>

        <div className="md:col-span-1 flex items-end">
          <button
            onClick={onAdd}
            className="w-full px-3 py-2 rounded bg-blue-600 hover:bg-blue-500"
          >
            + Add Rule
          </button>
        </div>
      </div>

      {/* list */}
      <div className="border border-slate-800 rounded overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs bg-slate-800/60">
          <div className="col-span-4">Item</div>
          <div className="col-span-2">Scope</div>
          <div className="col-span-3">Rule</div>
          <div className="col-span-1 text-center">Active</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {filteredRules.length === 0 && (
          <div className="px-3 py-4 text-sm text-slate-400">No rules yet for this view.</div>
        )}

        {filteredRules.map((r) => {
          const it = itemMap.get(r.itemId);
          const scopeLabel =
            r.scope === "global"
              ? "Global"
              : r.scope === "product"
              ? `Product ${r.productId || ""}`
              : `Batch ${r.batchId || ""}`;

          let ruleText = "";
          if (r.appliesAs === "perUnit") ruleText = `Per unit: ${r.qtyPerUnit}`;
          else if (r.appliesAs === "perBatch") ruleText = `Per batch: ${r.qtyPerBatch}`;
          else if (r.appliesAs === "threshold") {
            const t = r.threshold || {};
            ruleText = `Threshold: min ${t.minQty}, step ${t.step}, qty/step ${t.perStepQty}`;
          }

          return (
            <div key={r.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-800">
              <div className="col-span-4">
                <div className="text-sm font-medium">{it ? it.name : <span className="text-red-300">Missing item</span>}</div>
                <div className="text-xs text-slate-400">{it?.category || "—"}</div>
              </div>
              <div className="col-span-2 text-sm">{scopeLabel}</div>
              <div className="col-span-3 text-sm">
                {ruleText}
                {r.rounding && r.rounding !== "none" ? ` • ${r.rounding}` : ""}
                {r.notes ? ` • ${r.notes}` : ""}
              </div>
              <div className="col-span-1 flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={!!r.active}
                  onChange={(e) => updateRule(r.id, { active: e.target.checked })}
                />
              </div>
              <div className="col-span-2 flex items-center gap-2 justify-end">
                <button
                  onClick={() => onEdit(r)}
                  className="px-2 py-1 text-xs rounded bg-slate-700 hover:bg-slate-600"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteRule(r.id)}
                  className="px-2 py-1 text-xs rounded bg-red-600 hover:bg-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* modal */}
      {open && (
        <PackagingRuleModal
          items={items}
          initialRule={
            editing
              ? { ...editing }
              : {
                  scope, // prefill from page
                  itemId: "",
                  appliesAs: "perUnit",
                  qtyPerUnit: 1,
                  qtyPerBatch: 1,
                  threshold: null,
                  rounding: "none",
                  active: true,
                  notes: "",
                }
          }
          onSave={handleSave}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
